// Applications & Offers Firestore operations
import { readJobSource } from '../applications/jobSourceCapture';
import { ensureDb } from './ensureDb';
import type { ApplicationSubmissionPayload } from '../applications/applicationForm';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  Timestamp,
  getCountFromServer,
} from 'firebase/firestore';
import type { Application, Offer, ApplicationStatus, OfferStatus } from '../../types/applications';

/** Cap realtime listener payload size (most recent applications first). */
export const EMT_APPLICATIONS_LISTENER_MAX = 250;

export type ListenEmtApplicationsOptions = {
  /** Lower = less data per snapshot (e.g. dashboard). Clamped 15…{@link EMT_APPLICATIONS_LISTENER_MAX}. */
  queryLimit?: number;
};

function clampEmtApplicationsListenerLimit(requested?: number): number {
  const raw = requested ?? EMT_APPLICATIONS_LISTENER_MAX;
  return Math.min(EMT_APPLICATIONS_LISTENER_MAX, Math.max(15, Math.floor(raw)));
}

/**
 * Apply to a job (idempotent - uses Cloud Function)
 * This is the main entry point for applying to jobs
 */
/**
 * Jobs whose most recent applyToJob call in this tab found an application that already existed (the
 * function is idempotent and answers success with alreadyExists). The apply page reads it to tell a
 * real duplicate ("You already applied") from its own retry of a submission that did go through.
 */
const alreadyAppliedJobs = new Set<string>();
export function lastApplyFoundExisting(jobId: string): boolean {
  return alreadyAppliedJobs.has(jobId);
}

export async function applyToJob({
  jobId,
  emtId,
  emtProfile,
  answers,
  application,
}: {
  jobId: string;
  emtId: string;
  emtProfile: any;
  answers?: Array<{ questionId: string; answer: string | number }>;
  /** The multi-step application form. The function re-validates and cleans it before storing. */
  application?: ApplicationSubmissionPayload;
}): Promise<string> {
  if (import.meta.env.DEV) console.log('[APPLY] Starting apply via Cloud Function', { jobId, emtId });

  // Import Firebase Functions
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { app } = await import('../firebase');

  if (!app) {
    throw new Error('Firebase app not initialized');
  }

  const functions = getFunctions(app);
  const applyToJobFn = httpsCallable(functions, 'applyToJob');

  try {
    if (import.meta.env.DEV) console.log('[APPLY] Calling Cloud Function', { jobId, emtId });
    const result = await applyToJobFn({
      jobId,
      // Where they came from for this job (careers page, Google, job search, ...), remembered in this
      // tab when the job page was opened; the server normalizes it (src/lib/applications/applicationSource.ts).
      sourceChannel: readJobSource(jobId),
      ...(answers?.length ? { answers } : {}),
      ...(application ? { application } : {}),
    });
    const data = result.data as { applicationId: string; success?: boolean; alreadyExists?: boolean };
    if (data.alreadyExists === true) alreadyAppliedJobs.add(jobId);
    else alreadyAppliedJobs.delete(jobId);
    
    if (import.meta.env.DEV) console.log('[APPLY] Cloud Function success', { applicationId: data.applicationId, alreadyExists: data.alreadyExists });
    return data.applicationId;
  } catch (error: any) {
    console.error('[APPLY] Cloud Function error', {
      code: error.code,
      message: error.message,
      details: error.details,
      jobId,
      emtId
    });
    
    // Firebase client codes are often "functions/<code>"; match both forms.
    const code = String(error.code || '').replace(/^functions\//, '');
    // Every rethrow keeps the code (and details): the apply page's retry() decides from the code
    // whether a failure is transient or the server's final answer, which it must never re-send.
    const rethrow = (message: string): never => {
      const wrapped = new Error(message) as Error & { code?: string; details?: unknown };
      if (code) wrapped.code = code;
      wrapped.details = error.details;
      throw wrapped;
    };
    if (code === 'unauthenticated') {
      return rethrow('You must be logged in to apply for jobs.');
    } else if (code === 'not-found') {
      return rethrow('Job not found. It may have been removed.');
    } else if (code === 'failed-precondition') {
      // Keep the server's structured reason (e.g. the Documents Reviewed gaps) for the UI.
      return rethrow(error.message || 'This job is no longer accepting applications.');
    } else if (code === 'invalid-argument') {
      // The function names what is missing (a required answer, an incomplete application) — a
      // generic "invalid job information" left the candidate with nothing to act on.
      const reason = (error.details as { reason?: string } | undefined)?.reason;
      return rethrow(
        reason || /answer is required|asks a few questions|is required|valid /i.test(String(error.message))
          ? String(error.message)
          : 'Invalid job information. Please try again.',
      );
    } else {
      return rethrow(error.message || 'Failed to submit application. Please try again.');
    }
  }
}

/**
 * Get application by ID
 */
export async function getApplication(applicationId: string): Promise<Application | null> {
  const firestoreDb = ensureDb();
  const docRef = doc(firestoreDb, 'applications', applicationId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as Application;
}

/**
 * Get application by jobId and emtId (uses deterministic ID)
 */
export async function getMyApplicationForJob(emtId: string, jobId: string): Promise<Application | null> {
  const firestoreDb = ensureDb();

  // Query-only: a direct getDoc on applications/{jobId}_{emtId} when the doc does not exist still
  // evaluates security rules with no resource; top-level applications read requires resource.data.emtId,
  // which fails with permission denied. A filtered query returns only readable docs (or empty).
  const q = query(
    collection(firestoreDb, 'applications'),
    where('emtId', '==', emtId),
    where('jobId', '==', jobId),
    limit(5)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const detId = `${jobId}_${emtId}`;
  const preferred = snapshot.docs.find((d) => d.id === detId);
  const chosen = preferred ?? snapshot.docs[0];
  return { id: chosen.id, ...chosen.data() } as Application;
}

const ORG_JOBS_PER_QUERY = 4;

/**
 * Every application on the ORG's jobs, for any employer in the org (owner or teammate).
 *
 * listenEmployerApplications filters on employerId, which is whoever posted the job, so a teammate
 * saw "No applications yet" and had no Send Offer (QA 2026-10-06). The rules already let any
 * employer in the job's org read these (applications read rule: jobs/{jobId}.orgId == myOrgId());
 * a list query has to name the job, so this follows the org's jobs and queries `jobId in [...]`,
 * re-subscribing when a job is posted or removed. Only 4 jobs per query: the rule does exists() + get()
 * on each job plus a profile read, and Firestore allows 10 document reads per query evaluation, so
 * `in` with 5+ jobs is refused (production 2026-10-06: MedEx, 10 jobs, got "Failed to Load"). The poster's own employerId
 * query stays merged in, so nothing the old inbox showed can disappear. Newest first.
 */
export function listenOrgApplications(
  orgId: string | null | undefined,
  employerId: string,
  callback: (applications: Application[]) => void,
  onError?: (error: Error) => void
): () => void {
  if (!orgId) return listenEmployerApplications(employerId, callback, onError);
  const firestoreDb = ensureDb();
  const byId = new Map<string, Map<string, Application>>(); // source key -> applications
  let appUnsubs: Array<() => void> = [];
  let jobKey = '';
  const millis = (v: unknown): number => {
    const t = v as { toMillis?: () => number; seconds?: number } | null | undefined;
    return t?.toMillis?.() ?? (typeof t?.seconds === 'number' ? t.seconds * 1000 : 0);
  };
  const emit = () => {
    const merged = new Map<string, Application>();
    for (const m of byId.values()) for (const [id, a] of m) merged.set(id, a);
    callback([...merged.values()].sort((a, b) => millis((b as { createdAt?: unknown }).createdAt) - millis((a as { createdAt?: unknown }).createdAt)));
  };
  const fail = (err: { code?: string; message?: string }) => {
    const e = new Error(err.code === 'permission-denied' ? 'Permission denied: Unable to load applications' : `Failed to load applications: ${err.message}`);
    e.name = err.code === 'permission-denied' ? 'PermissionError' : 'QueryError';
    if (onError) onError(e); else callback([]);
  };
  // A failing org chunk must never blank the inbox: the poster's own query ('own') still answers.
  const listen = (key: string, q: ReturnType<typeof query>, onFail: (err: { code?: string; message?: string }) => void = fail) =>
    onSnapshot(
      q,
      (snap) => {
        byId.set(key, new Map(snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const status = typeof data.status === 'string' ? data.status : (data.status as { name?: unknown } | undefined)?.name;
          return [d.id, { id: d.id, ...data, status: (status ? String(status) : 'new') as ApplicationStatus } as Application];
        })));
        emit();
      },
      onFail
    );
  const own = listen('own', query(collection(firestoreDb, 'applications'), where('employerId', '==', employerId)));
  const jobsUnsub = onSnapshot(
    query(collection(firestoreDb, 'jobs'), where('orgId', '==', orgId)),
    (snap) => {
      const ids = snap.docs.map((d) => d.id).sort();
      const key = ids.join(',');
      if (key === jobKey) return;
      jobKey = key;
      appUnsubs.forEach((u) => u());
      appUnsubs = [];
      for (const k of [...byId.keys()]) if (k !== 'own') byId.delete(k);
      for (let i = 0; i < ids.length; i += ORG_JOBS_PER_QUERY) {
        const chunk = ids.slice(i, i + ORG_JOBS_PER_QUERY);
        appUnsubs.push(
          listen(`jobs:${i}`, query(collection(firestoreDb, 'applications'), where('jobId', 'in', chunk)), (err) => {
            console.warn('[listenOrgApplications] org jobs query failed; showing the rest', err?.code);
            byId.delete(`jobs:${i}`);
            emit();
          })
        );
      }
      emit();
    },
    fail
  );
  return () => {
    own();
    jobsUnsub();
    appUnsubs.forEach((u) => u());
  };
}

/**
 * List applications for employer (with optional status filter)
 * Returns unsubscribe function and error callback
 * Includes fallback query for when indexes are building
 */
export function listenEmployerApplications(
  employerId: string,
  callback: (applications: Application[]) => void,
  onError?: (error: Error) => void,
  statusFilter?: ApplicationStatus
): () => void {
  const firestoreDb = ensureDb();
  
  // Helper to normalize status to string
  const normalizeStatus = (data: any): ApplicationStatus => {
    const status = data.status;
    if (typeof status === 'string') {
      return status as ApplicationStatus;
    }
    if (status && typeof status === 'object' && 'name' in status) {
      return String(status.name) as ApplicationStatus;
    }
    return 'new';
  };
  
  // Primary query with orderBy (requires composite index)
  let primaryQuery = query(
    collection(firestoreDb, 'applications'),
    where('employerId', '==', employerId),
    orderBy('createdAt', 'desc')
  );
  
  if (statusFilter) {
    primaryQuery = query(primaryQuery, where('status', '==', statusFilter));
  }
  
  // Fallback query without orderBy (no composite index needed)
  let fallbackQuery = query(
    collection(firestoreDb, 'applications'),
    where('employerId', '==', employerId)
  );
  
  if (statusFilter) {
    fallbackQuery = query(fallbackQuery, where('status', '==', statusFilter));
  }
  
  let usingFallback = false;
  let unsubscribePrimary: (() => void) | null = null;
  
  const tryPrimary = () => {
    if (usingFallback) return; // Already using fallback, don't retry primary
    usingFallback = false;
    
    // Clean up any existing listeners
    if (unsubscribePrimary) {
      unsubscribePrimary();
      unsubscribePrimary = null;
    }
    if (unsubscribeFallback) {
      unsubscribeFallback();
      unsubscribeFallback = null;
    }
    
    unsubscribePrimary = onSnapshot(
      primaryQuery,
      (snapshot) => {
        // Success - we're using the indexed query
        const applications = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            status: normalizeStatus(data), // Normalize status to string
          } as Application;
        });
        callback(applications);
      },
      (error) => {
        console.error('[listenEmployerApplications] Primary query error:', error);
        
        // Check if it's an index building error
        const isIndexBuilding = error.code === 'failed-precondition' && 
          (error.message?.includes('currently building') || 
           error.message?.includes('requires an index') ||
           error.message?.includes('index is currently building'));
        
        if (isIndexBuilding && !usingFallback) {
          console.warn('[listenEmployerApplications] Index building, switching to fallback query');
          // Unsubscribe from primary before starting fallback
          if (unsubscribePrimary) {
            unsubscribePrimary();
            unsubscribePrimary = null;
          }
          usingFallback = true;
          // Try fallback query
          tryFallback();
          return;
        }
        
        // Check for other index errors
        if (error.code === 'failed-precondition' && !usingFallback) {
          console.warn('[listenEmployerApplications] Index error, switching to fallback query');
          // Unsubscribe from primary before starting fallback
          if (unsubscribePrimary) {
            unsubscribePrimary();
            unsubscribePrimary = null;
          }
          usingFallback = true;
          tryFallback();
          return;
        }
        
        // Check for permission error
        if (error.code === 'permission-denied') {
          const errorMsg = new Error('Permission denied: Unable to load applications');
          errorMsg.name = 'PermissionError';
          if (onError) {
            onError(errorMsg);
          } else {
            callback([]);
          }
          return;
        }
        
        // Other errors
        const errorMsg = new Error(`Failed to load applications: ${error.message}`);
        errorMsg.name = 'QueryError';
        if (onError) {
          onError(errorMsg);
        } else {
          callback([]);
        }
      }
    );
  };
  
  let unsubscribeFallback: (() => void) | null = null;
  
  const tryFallback = () => {
    if (!usingFallback) return; // Only use fallback if we've determined we need it
    
    // Clean up any existing listeners
    if (unsubscribeFallback) {
      unsubscribeFallback();
      unsubscribeFallback = null;
    }
    
    unsubscribeFallback = onSnapshot(
      fallbackQuery,
      (snapshot) => {
        // Sort client-side by createdAt desc
        const applications = (snapshot.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              status: normalizeStatus(data), // Normalize status to string
            };
          }) as Application[])
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
            return bTime - aTime; // Descending
          });
        callback(applications);
      },
      (error) => {
        console.error('[listenEmployerApplications] Fallback query error:', error);
        const errorMsg = new Error(`Failed to load applications: ${error.message}`);
        errorMsg.name = 'QueryError';
        if (onError) {
          onError(errorMsg);
        } else {
          callback([]);
        }
      }
    );
  };
  
  // Start with primary query
  tryPrimary();
  
  // Return unsubscribe function
  return () => {
    if (unsubscribePrimary) unsubscribePrimary();
    if (unsubscribeFallback) unsubscribeFallback();
  };
}

/**
 * List applications for EMT
 * Returns unsubscribe function and error callback
 * Includes fallback query for when indexes are building
 */
export function listenEmtApplications(
  emtId: string,
  callback: (applications: Application[]) => void,
  onError?: (error: Error) => void,
  statusFilter?: ApplicationStatus,
  options?: ListenEmtApplicationsOptions
): () => void {
  const firestoreDb = ensureDb();
  const listenerLimit = clampEmtApplicationsListenerLimit(options?.queryLimit);

  // Helper to normalize status to string
  const normalizeStatus = (data: any): ApplicationStatus => {
    const status = data.status;
    if (typeof status === 'string') {
      return status as ApplicationStatus;
    }
    if (status && typeof status === 'object' && 'name' in status) {
      return String(status.name) as ApplicationStatus;
    }
    return 'new';
  };
  
  // Primary query with orderBy (requires composite index)
  const primaryQuery = statusFilter
    ? query(
        collection(firestoreDb, 'applications'),
        where('emtId', '==', emtId),
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc'),
        limit(listenerLimit)
      )
    : query(
        collection(firestoreDb, 'applications'),
        where('emtId', '==', emtId),
        orderBy('createdAt', 'desc'),
        limit(listenerLimit)
      );

  // Fallback query without orderBy (no composite index needed)
  const fallbackQuery = statusFilter
    ? query(
        collection(firestoreDb, 'applications'),
        where('emtId', '==', emtId),
        where('status', '==', statusFilter),
        limit(listenerLimit)
      )
    : query(
        collection(firestoreDb, 'applications'),
        where('emtId', '==', emtId),
        limit(listenerLimit)
      );
  
  let usingFallback = false;
  let unsubscribePrimary: (() => void) | null = null;
  
  const tryPrimary = () => {
    if (usingFallback) return; // Already using fallback, don't retry primary
    usingFallback = false;
    
    // Clean up any existing listeners
    if (unsubscribePrimary) {
      unsubscribePrimary();
      unsubscribePrimary = null;
    }
    if (unsubscribeFallback) {
      unsubscribeFallback();
      unsubscribeFallback = null;
    }
    
    unsubscribePrimary = onSnapshot(
      primaryQuery,
      (snapshot) => {
        // Success - we're using the indexed query
        const applications = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            status: normalizeStatus(data), // Normalize status to string
          } as Application;
        });
        callback(applications);
      },
      (error) => {
        console.error('[listenEmtApplications] Primary query error:', error);
        
        // Check if it's an index building error
        const isIndexBuilding = error.code === 'failed-precondition' && 
          (error.message?.includes('currently building') || 
           error.message?.includes('requires an index') ||
           error.message?.includes('index is currently building'));
        
        if (isIndexBuilding && !usingFallback) {
          console.warn('[listenEmtApplications] Index building, switching to fallback query');
          // Unsubscribe from primary before starting fallback
          if (unsubscribePrimary) {
            unsubscribePrimary();
            unsubscribePrimary = null;
          }
          usingFallback = true;
          // Try fallback query
          tryFallback();
          return;
        }
        
        // Check for other index errors
        if (error.code === 'failed-precondition' && !usingFallback) {
          console.warn('[listenEmtApplications] Index error, switching to fallback query');
          // Unsubscribe from primary before starting fallback
          if (unsubscribePrimary) {
            unsubscribePrimary();
            unsubscribePrimary = null;
          }
          usingFallback = true;
          tryFallback();
          return;
        }
        
        // Check for permission error
        if (error.code === 'permission-denied') {
          const errorMsg = new Error('Permission denied: Unable to load applications');
          errorMsg.name = 'PermissionError';
          if (onError) {
            onError(errorMsg);
          } else {
            callback([]);
          }
          return;
        }
        
        // Other errors
        const errorMsg = new Error(`Failed to load applications: ${error.message}`);
        errorMsg.name = 'QueryError';
        if (onError) {
          onError(errorMsg);
        } else {
          callback([]);
        }
      }
    );
  };
  
  let unsubscribeFallback: (() => void) | null = null;
  
  const tryFallback = () => {
    if (!usingFallback) return; // Only use fallback if we've determined we need it
    
    // Clean up any existing listeners
    if (unsubscribeFallback) {
      unsubscribeFallback();
      unsubscribeFallback = null;
    }
    
    unsubscribeFallback = onSnapshot(
      fallbackQuery,
      (snapshot) => {
        // Sort client-side by createdAt desc
        const applications = (snapshot.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              status: normalizeStatus(data), // Normalize status to string
            };
          }) as Application[])
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
            const bTime = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
            return bTime - aTime; // Descending
          });
        callback(applications);
      },
      (error) => {
        console.error('[listenEmtApplications] Fallback query error:', error);
        const errorMsg = new Error(`Failed to load applications: ${error.message}`);
        errorMsg.name = 'QueryError';
        if (onError) {
          onError(errorMsg);
        } else {
          callback([]);
        }
      }
    );
  };
  
  // Start with primary query
  tryPrimary();
  
  // Return unsubscribe function
  return () => {
    if (unsubscribePrimary) unsubscribePrimary();
    if (unsubscribeFallback) unsubscribeFallback();
  };
}

/**
 * Update application status (employer action)
 */
export async function updateApplicationStatus(
  applicationId: string,
  status: ApplicationStatus,
  employerId: string
): Promise<void> {
  const firestoreDb = ensureDb();
  const applicationRef = doc(firestoreDb, 'applications', applicationId);
  await updateDoc(applicationRef, {
    status,
    updatedAt: serverTimestamp(),
    lastActionAt: serverTimestamp(),
    lastActionBy: 'employer' as const,
  });
}

/**
 * Employer requests a video introduction from an applicant who didn't include one.
 * The onApplicationVideoRequested Cloud Function trigger notifies the EMT.
 */
export async function requestVideoIntro(applicationId: string, employerId: string): Promise<void> {
  const firestoreDb = ensureDb();
  const applicationRef = doc(firestoreDb, 'applications', applicationId);
  await updateDoc(applicationRef, {
    'videoIntro.status': 'requested',
    'videoIntro.requestedAt': serverTimestamp(),
    'videoIntro.requestedBy': employerId,
    isUnreadForEmt: true,
  });
}

/**
 * Mark application as read (employer)
 */
export async function markApplicationReadForEmployer(applicationId: string): Promise<void> {
  const firestoreDb = ensureDb();
  const applicationRef = doc(firestoreDb, 'applications', applicationId);
  await updateDoc(applicationRef, {
    isUnreadForEmployer: false,
  });
}

/**
 * Mark application as read (EMT)
 */
export async function markApplicationReadForEmt(applicationId: string): Promise<void> {
  const firestoreDb = ensureDb();
  const applicationRef = doc(firestoreDb, 'applications', applicationId);
  await updateDoc(applicationRef, {
    isUnreadForEmt: false,
  });
}

/**
 * Create an offer.
 * Never send undefined to Firestore (addDoc rejects it).
 * Optional jobTitle and employerName stored for EMT offer list display (avoids N+1 reads).
 */
export async function createOffer(data: {
  applicationId: string;
  jobId: string;
  employerId: string;
  emtId: string;
  message: string;
  pay?: string;
  schedule?: string;
  jobTitle?: string;
  employerName?: string;
}): Promise<string> {
  const firestoreDb = ensureDb();
  const payload: Record<string, unknown> = {
    applicationId: data.applicationId,
    jobId: data.jobId,
    employerId: data.employerId,
    emtId: data.emtId,
    message: (data.message || '').trim(),
    status: 'sent' as OfferStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (data.pay != null && String(data.pay).trim()) {
    payload.pay = String(data.pay).trim();
  }
  if (data.schedule != null && String(data.schedule).trim()) {
    payload.schedule = String(data.schedule).trim();
  }
  if (data.jobTitle != null && String(data.jobTitle).trim()) {
    payload.jobTitle = String(data.jobTitle).trim();
  }
  if (data.employerName != null && String(data.employerName).trim()) {
    payload.employerName = String(data.employerName).trim();
  }
  // Firestore rejects undefined; ensure no undefined keys
  const sanitized: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v !== undefined) sanitized[k] = v;
  }
  const offersRef = collection(firestoreDb, 'offers');
  const docRef = await addDoc(offersRef, sanitized);
  return docRef.id;
}

/**
 * Subscribe to offers for an EMT (real-time list for Offers tab).
 * EMT can read offers where emtId == request.auth.uid (Firestore rules).
 */
export function listenEmtOffers(
  emtId: string,
  callback: (offers: Offer[]) => void,
  onError?: (err: Error) => void
): () => void {
  const firestoreDb = ensureDb();
  const q = query(
    collection(firestoreDb, 'offers'),
    where('emtId', '==', emtId),
    orderBy('createdAt', 'desc'),
    limit(100)
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const offers = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Offer[];
      callback(offers);
    },
    (err) => {
      if (import.meta.env.DEV) {
        console.warn('[listenEmtOffers] Error:', err);
      }
      onError?.(err);
      callback([]);
    }
  );
}

function newestOffer(docs: Array<{ id: string; data(): unknown }>): Offer | null {
  const ms = (o: { createdAt?: any }) => o.createdAt?.toMillis?.() ?? (typeof o.createdAt?.seconds === 'number' ? o.createdAt.seconds * 1000 : 0);
  const offers = docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Offer);
  offers.sort((a, b) => ms(b as any) - ms(a as any));
  return offers[0] ?? null;
}

/**
 * The newest offer on one application, as the candidate sees it.
 *
 * The offers read rule is not a filter: it admits a row when emtId or employerId is the reader, or
 * when the row's job belongs to the reader's org. A query on applicationId alone proves none of
 * those, so Firestore rejected it for every EMT — the error was swallowed and Accept / Decline never
 * appeared. emtId == the candidate is the clause the rule checks. Equality filters only (sorted
 * here), so no composite index is needed. Throws on failure; never returns "no offer" for an error.
 */
export async function getOfferByApplicationForEmt(applicationId: string, emtUid: string): Promise<Offer | null> {
  const firestoreDb = ensureDb();
  const snapshot = await getDocs(
    query(collection(firestoreDb, 'offers'), where('emtId', '==', emtUid), where('applicationId', '==', applicationId))
  );
  return newestOffer(snapshot.docs);
}

/**
 * The newest offer on one application, as the employer side sees it. jobId == the application's job
 * pins the rule's get() on the job, so it is approved for the employer who posted the job and for
 * colleagues in the same org (an employerId filter would only cover the poster). Throws on failure.
 */
export async function getOfferByApplicationForEmployer(applicationId: string, jobId: string): Promise<Offer | null> {
  if (!jobId) throw new Error('This application has no job, so its offer cannot be looked up.');
  const firestoreDb = ensureDb();
  const snapshot = await getDocs(
    query(collection(firestoreDb, 'offers'), where('jobId', '==', jobId), where('applicationId', '==', applicationId))
  );
  return newestOffer(snapshot.docs);
}

/** One offer by id (the rule allows a get to its candidate, its employer and the job's org). */
export async function getOfferById(offerId: string): Promise<Offer | null> {
  const firestoreDb = ensureDb();
  const snap = await getDoc(doc(firestoreDb, 'offers', offerId));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as Offer) : null;
}

/**
 * Update offer status (EMT accept/decline)
 */
export async function updateOfferStatus(
  offerId: string,
  status: 'accepted' | 'declined',
  emtId: string
): Promise<void> {
  const firestoreDb = ensureDb();
  const offerRef = doc(firestoreDb, 'offers', offerId);
  await updateDoc(offerRef, {
    status,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get unread applications count for employer
 */
export async function getUnreadApplicationsCount(employerId: string): Promise<number> {
  const firestoreDb = ensureDb();
  const q = query(
    collection(firestoreDb, 'applications'),
    where('employerId', '==', employerId),
    where('isUnreadForEmployer', '==', true)
  );
  try {
    const countSnap = await getCountFromServer(q);
    return countSnap.data().count;
  } catch {
    const snapshot = await getDocs(q);
    return snapshot.size;
  }
}

/**
 * Get unread applications count for EMT
 */
export async function getUnreadApplicationsCountForEmt(emtId: string): Promise<number> {
  const firestoreDb = ensureDb();
  const q = query(
    collection(firestoreDb, 'applications'),
    where('emtId', '==', emtId),
    where('isUnreadForEmt', '==', true)
  );
  try {
    const countSnap = await getCountFromServer(q);
    return countSnap.data().count;
  } catch {
    const snapshot = await getDocs(q);
    return snapshot.size;
  }
}
